import React, { useCallback, useEffect, useState } from 'react';
import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import { FileSystem } from 'new/components/flows/configure/FileSystem';
import { useDirectFlow } from 'new/components/flows/types/DirectFlow/hooks/useDirectFlow';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import useConfig from 'new/components/flows/types/DirectFlow/hooks/useConfig';
import { useForm } from 'new/elements/Form/useForm';
import useRequests from 'new/state/request/repository/useRequests';
import createFlowService from 'new/components/flows/service/createFlowService';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useHistory } from 'react-router-dom';
import { Base64 } from 'js-base64';
import { createCodeContainer, updateCodeContainer, setError } from 'js/actions';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_NOSQL,
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
} from 'js/constants/connectionCategories';
import Database from 'new/components/flows/configure/Database';
import createDatabaseService from 'new/components/flows/configure/Database/service/createDatabaseService';
import { NoSQL } from 'new/components/flows/configure/NoSQL/NoSQL';
import { validateNoSQLSourceConfig } from 'new/components/flows/configure/NoSQL/NoSQL.utils';
import { RestAPIUtil } from 'new/components/flows/configure/RestAPISource/RestAPISource.utils';
import useProbeAdapter from 'new/state/probe/adapter/useProbeAdapter';
import { RestAPISourceProvider } from 'new/components/flows/configure/RestAPISource/RestAPISourceProvider';
import { tryCatch, tryCatchAsync } from 'new/utils/TryCatch';
import { TransformType } from 'new/components/ReusableTransform/types';
import useFormsAdapter from 'new/state/forms/adapter/useFormsAdapter';
import { usePreviousPath } from 'new/hooks/usePreviousPath';
import usePaging from 'new/state/paging/repository/usePaging';
import { useDispatch } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import flowTypes from '../../flowTypes';
import useFlowType from '../hooks/useFlowType';
import FileDataFormatSection from './FileDataFormatSection/FileDataFormatSection';
import RawFileDataCheckbox from 'js/components/data_sources/add_ds_form/RawFileDataCheckbox';
import useFlowParams from 'new/components/flows/useFlowParams';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { useDirectFlowSaveConfirmation } from '../hooks/useDirectFlowSaveConfirmation';
import { useBYOSaveConfirmation } from '../hooks/useBYOSaveConfirmation';
import { shouldRenderRunAction } from 'js/components/pipelines/cards/components/ExpandActions/utils/shouldRenderSourceAction';
import { CustomUserConfirmation } from 'js/components/route_utils/UserConfirmation';
import { useChangeDetection } from '../hooks/useChangeDetection';
import {
  assignFlowToProjectIfPresent,
  redirectToProjectIfNeeded,
} from 'new/utils/projectAssignment';
import { useSelector } from 'react-redux';
import { getCurrentProjectId } from 'js/reducers/app/selectors';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { isUiModeCode } from 'new/utils/uiMode';
import { UiMode } from 'new/constants/uiMode';

const FORM = FORM_TYPE_CREATE_DIRECT_FLOW;
const databaseService = createDatabaseService();
const { createDefaultSourceConfig, getSourceName } = createFlowService();

export const SourceConfigureStep = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const currentProjectId = useSelector(getCurrentProjectId);
  const history = useHistory();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { showError } = useRequests();
  const organization = useCurrentOrg();
  const adapter = useFormsAdapter(FORM);
  const { getProbeSample } = useProbeAdapter();
  const { overrideNextFunction, nextPage } = useForm();
  const { sourceConfig, onSourceConfigChanged, onApiSourceConfigChanged } = useConfig();
  const { paging, getSourceConfigFields, getConnectionCategoryByCredID } = usePaging();
  const flowType = useFlowType();
  const { data: formData, onBucketSelected, validate } = useFlowsSetup(FORM, flowType);

  const { isModal, onDismiss } = useFlowParams();

  const previousPath = usePreviousPath();
  const flowService = createFlowService();

  const onError = useCallback((msg: string) => showError(msg), []);

  const {
    selectedConnection,
    selectedBucket,
    selectedCredential,
    connectionCategory,
    credentialId,
    source,
    sourceConfigFields,
    isEditMode,
    table: selectedTable,
    onTableSelected,
    connectionType,
  } = useDirectFlow();

  // Get ALL fields (before flow type exclusion) to ensure fields like ai.function
  // can be added when they should render, even if excluded for the flow type
  // reduceConfig will handle the final filtering based on flow type exclusion
  const allSourceConfigFields = paging.allSourceConfigById?.[selectedConnection] || [];

  // Save confirmation logic - use BYO-specific hook for custom flow type
  const directFlowSaveConfirmation = useDirectFlowSaveConfirmation();
  const byoSaveConfirmation = useBYOSaveConfirmation();

  // Use BYO save confirmation for custom flow type, otherwise use DirectFlow
  const saveConfirmation =
    flowType === flowTypes.custom ? byoSaveConfirmation : directFlowSaveConfirmation;

  const {
    showSaveConfirmation,
    setShowSaveConfirmation,
    isSaving,
    pendingSaveRef,
    currentRunWithIngestion,
    handleSaveConfirmation,
    onCloseSaveConfirmation,
    setShowLeaveConfirmation,
    overrideNextFunction: overrideFormNextFunction,
  } = saveConfirmation;

  // Get change detection at the component level
  const { hasChanges: currentHasChanges } = useChangeDetection();

  const createSource = async (sourceInfo: any) => {
    const credentialCategory = getConnectionCategoryByCredID(credentialId);
    const sourceType =
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API
        ? credentialCategory
        : selectedConnection;

    const { error, data } = await tryCatchAsync(
      adapter.createSource({
        sourceInfo,
        credentialId,
        connectionCategory,
        sourceType,
      }),
    );

    if (error) return { error: true, message: error.message };

    return { data: data?.payload?.formData?.source };
  };

  const updateSource = async (idSource: number, sourceInfo: any, runWithIngestion?: boolean) => {
    sourceInfo.id = idSource;
    const credentialCategory = getConnectionCategoryByCredID(credentialId);
    const sourceType =
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API
        ? credentialCategory
        : selectedConnection;

    const { error, data } = await tryCatchAsync(
      adapter.updateSource({
        sourceInfo,
        credentialId,
        connectionCategory,
        sourceType,
        credentialGroupId: source?.data_credentials_group_id ?? null,
        runWithIngestion: runWithIngestion ?? (formData.runWithIngestion || false),
      }),
    );

    if (error) return { error: true, message: error.message };

    return { data: data?.payload?.formData?.source };
  };

  const getCustomProcessorId = async () => {
    const { customProcessorId, customProcessorCode, packages, extra_data } = sourceConfig;

    if (sourceConfig['ui.use_custom_processor']) {
      if (sourceConfig['ui.custom_processor_mode'] === 'custom_code') {
        const payload = {
          name: 'Custom file processor for source',
          resource_type: 'source_custom',
          output_type: TransformType.CUSTOM,
          code_type: 'python',
          code_encoding: 'base64',
          code: Base64.encode(customProcessorCode),
          code_config: {
            packages: packages || '',
            extra_data: extra_data || '',
          },
        };
        const shouldUpdate =
          isEditMode &&
          source?.source_config?.['ui.use_custom_processor'] &&
          source.source_config['ui.custom_processor_mode'] === 'custom_code' &&
          source.code_container_id;
        const response = await dispatch(
          shouldUpdate
            ? updateCodeContainer(source.code_container_id, payload)
            : createCodeContainer(payload),
        );

        return response.result;
      }

      return customProcessorId || null;
    }

    return null;
  };

  const getName = () => {
    let { name, sourceName } = sourceConfig;
    return (
      name ||
      sourceName ||
      getSourceName({
        selectedBucket,
        selectedTable,
        selectedCredential,
        connectionCategory,
      }) ||
      (source?.id ? selectedConnection : undefined)
    );
  };

  const buildSourceInfo = (codeContainerId: number) => {
    let { description, sourceDescription } = sourceConfig;

    let defaultSourceConfig = createDefaultSourceConfig({
      sourceConfig,
      selectedConnection,
      orgId: organization.id,
      configFields: sourceConfigFields,
    });

    switch (connectionCategory) {
      case CONNECTION_CATEGORY_DATABASE:
        const { error: errorBuildSourceConfig, data } = tryCatch(() =>
          databaseService.buildSourceConfig({
            sourceConfig,
            configFields: sourceConfigFields,
          }),
        );

        if (errorBuildSourceConfig) {
          throw new Error(errorBuildSourceConfig.message);
        }

        defaultSourceConfig = data;

        break;

      case CONNECTION_CATEGORY_NOSQL:
        if (!selectedBucket) {
          showError('Please select a collection to scan.');
          return;
        }
        const fields = getSourceConfigFields(selectedConnection, flowType);
        const { error, message } = validateNoSQLSourceConfig(sourceConfig, fields);
        if (error) {
          setError(message);
          return;
        }

        break;

      case CONNECTION_CATEGORY_REST:
      case CONNECTION_CATEGORY_TEMPLATIZED_API:
        const credID = formData.credentialId;
        const templatizedConfig = formData?.templatizedAPIConfig;

        const { newSourceConfig } = RestAPIUtil.builders.buildConfigToCreateRestAPISource({
          paging,
          organization,
          selectedConnection,
          connectionCategory,
          apiConfig: formData?.apiConfig,
          sourceConfig: defaultSourceConfig,
          templatizedAPIConfig: templatizedConfig,
          configFields: getSourceConfigFields(selectedConnection),
        });

        defaultSourceConfig = newSourceConfig;
        defaultSourceConfig?.['rest.iterations']?.forEach((__: any, idx: number) =>
          getProbeSample(credID, idx, defaultSourceConfig),
        );

        if (connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API) {
          if (defaultSourceConfig?.template_config) {
            const uiMode = isUiModeCode(sourceConfig) ? UiMode.CODE : UiMode.VIEW;
            defaultSourceConfig.template_config['ui.mode'] = uiMode;
          }
        }

        break;
      default:
        break;
    }

    // NoSQL query/timestamp modes select backend filtering as well as form fields.
    if (
      connectionCategory !== CONNECTION_CATEGORY_NOSQL ||
      defaultSourceConfig.mode === 'default'
    ) {
      delete defaultSourceConfig.mode;
    }
    delete defaultSourceConfig.name;
    delete defaultSourceConfig.description;
    delete defaultSourceConfig.sourceName;
    delete defaultSourceConfig.sourceDescription;
    delete defaultSourceConfig.customProcessorCode;
    delete defaultSourceConfig.customProcessorId;

    let config = {
      source_config: defaultSourceConfig,
    } as any;

    if (connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API)
      if (!formData?.apiConfig?.advanced_templatized_api) {
        /**
         * { vendor_endpoint_id: number, template_config:{} }
         */
        config = defaultSourceConfig;
      }

    config.code_container_id = codeContainerId;

    if (
      connectionCategory === CONNECTION_CATEGORY_FILE &&
      defaultSourceConfig['ui.use_custom_processor'] &&
      flowType === flowTypes.custom
    ) {
      config.source_config['custom_runtime.mode'] = 'REPLICATION';
    }

    return {
      name: getName(),
      ...config,
      flow_type: flowType,
      description: description ?? sourceDescription,
    };
  };

  const handleSourceCreationOrUpdate = async (
    runWithIngestion?: boolean,
  ): Promise<{ id?: number }> => {
    const codeContainerId = await getCustomProcessorId();
    const sourceInfo = buildSourceInfo(codeContainerId);

    const { error, data, message } = source?.id
      ? await updateSource(source?.id, sourceInfo, runWithIngestion)
      : await createSource(sourceInfo);

    if (error) {
      console.error('Error while creating/updating source info:', message);
      return null;
    }
    return data;
  };

  const handleSourceNavigation = async (history: any, newSource: any) => {
    const { id = '' } = newSource;
    if (!!id) {
      if (isModal) {
        onDismiss({ addedDataSourceId: id, ...newSource });
        return;
      }
      if (source?.id) {
        // Editing existing source - just go back
        history.goBack();
      } else {
        // Creating new source - navigate to edit screen if it created a new flow
        try {
          const flowNodeId = newSource?.origin_node_id || newSource?.flow_node_id;
          const assignedProjectId = await assignFlowToProjectIfPresent({
            dispatch,
            flowNodeId,
            currentProjectId,
          });
          if (assignedProjectId) {
            setIsRedirecting(true);
            redirectToProjectIfNeeded(history, assignedProjectId, dispatch);
            return;
          }
        } catch (error) {
          console.error('Failed to assign flow to project:', error);
          setIsRedirecting(false);
        }

        const flowOriginId = newSource?.origin_node_id || null;
        history.replace(
          flowService.getPathToGoAfterCreation(previousPath, undefined, {
            resourceType: 'source',
            flowType,
            flowOriginId,
          }),
        );
      }
    }
  };

  async function createSourceNextStep(): Promise<boolean> {
    const invalidForm = !validate(showError);
    if (invalidForm) return false;

    // Check if we should show save confirmation
    const shouldShow = !!(
      source && // We have a source object
      source?.status === 'ACTIVE' && // Source is active
      shouldRenderRunAction(source, connectionCategory) &&
      currentHasChanges && // Only show confirmation if there are actual changes
      !isSaving
    );

    if (shouldShow) {
      setShowSaveConfirmation(true);
      // Store the save function to call later and return a promise that resolves when save completes
      return new Promise<boolean>((resolve) => {
        pendingSaveRef.current = async (runWithIngestion: boolean) => {
          const result = await performSaveWithRunWithIngestion(runWithIngestion);
          if (result) {
            setShowLeaveConfirmation(false);
          }
          resolve(result);
          return result;
        };
      });
    }

    // No save confirmation needed, proceed with normal save
    const result = await performSaveWithRunWithIngestion(currentRunWithIngestion);

    // If save was successful, complete the form flow to prevent exit modal
    if (result) {
      setShowLeaveConfirmation(false);
    }

    return result;
  }

  const performSaveWithRunWithIngestion = async (runWithIngestion: boolean): Promise<boolean> => {
    const createdSource = await handleSourceCreationOrUpdate(runWithIngestion);
    if (createdSource) {
      handleSourceNavigation(history, createdSource);
      return Boolean(createdSource.id);
    }
    return false;
  };

  const onSaveModeAdvanced = (newSourceConfig: any) => {
    onSourceConfigChanged(newSourceConfig);
  };

  const onSaveModeAdvancedApi = (newApiConfig: any) => {
    onApiSourceConfigChanged(newApiConfig);
  };

  useEffect(() => {
    overrideFormNextFunction(async () => await createSourceNextStep());
  }, [createSourceNextStep, overrideFormNextFunction, currentHasChanges]);

  useEffect(() => {
    return () => {
      overrideNextFunction(null);
    };
  }, []);

  if (connectionType === 'SINK') return null;

  if (!sourceConfig) return <CenteredLoader />;
  if (isRedirecting) return <CenteredLoader />;

  // Render main content
  let mainContent: React.JSX.Element;
  if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
    mainContent = (
      <Database
        source={source}
        formType={FORM_TYPE_CREATE_DIRECT_FLOW}
        flowType={flowType}
        isEditMode={isEditMode}
        selectedTable={selectedTable}
        selectedBucket={selectedBucket}
        onTableSelected={onTableSelected}
        selectedConnection={selectedConnection}
        onSourceConfigChanged={onSourceConfigChanged}
        credentialId={credentialId}
        sourceConfig={sourceConfig}
        connectionType={connectionType}
        onSaveAdvancedMode={onSaveModeAdvanced}
        nextFormStage={nextPage}
      />
    );
  } else if (connectionCategory === CONNECTION_CATEGORY_NOSQL) {
    mainContent = (
      <NoSQL
        flowType={flowType}
        formType={FORM_TYPE_CREATE_DIRECT_FLOW}
        sourceConfig={sourceConfig}
        selectedBucket={selectedBucket}
        selectedCredential={selectedCredential}
        selectedConnection={selectedConnection}
        onSourceConfigChanged={onSourceConfigChanged}
        onSaveAdvancedMode={onSaveModeAdvanced}
        nextFormStage={nextPage}
        onError={onError}
      />
    );
  } else if (
    connectionCategory === CONNECTION_CATEGORY_REST ||
    connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API
  ) {
    mainContent = (
      <RestAPISourceProvider
        flowType={flowType}
        formType={FORM_TYPE_CREATE_DIRECT_FLOW}
        sourceConfig={sourceConfig}
        apiConfig={formData.apiConfig}
        selectedCredential={selectedCredential}
        selectedConnection={selectedConnection}
        onSourceConfigChanged={onApiSourceConfigChanged}
        onSaveAdvancedMode={onSaveModeAdvancedApi}
        nextFormStage={nextPage}
        onError={onError}
      />
    );
  } else {
    mainContent = (
      <FileSystem
        source={source}
        formType={FORM}
        shouldShowLoading
        flowType={flowType}
        connectionType={connectionType}
        selectedBucket={selectedBucket}
        selectedConnection={selectedConnection}
        credentialId={credentialId}
        sourceConfig={sourceConfig}
        renderSectionFields={(section, fields) => (
          <>
            <RawFileDataCheckbox
              section={section}
              sourceConfig={sourceConfig}
              sourceConfigFields={allSourceConfigFields}
              onSourceConfigChanged={onSourceConfigChanged}
            />
            <FileDataFormatSection
              section={section}
              fields={fields}
              sourceConfig={sourceConfig}
              buildSourceInfo={buildSourceInfo}
              onSourceConfigChanged={onSourceConfigChanged}
              getProbeSampleCallback={getProbeSample}
              useFlexFlowCustomSourceConfig={null}
              getCustomProcessorId={getCustomProcessorId}
            />
          </>
        )}
        onBucketSelected={onBucketSelected}
        onSourceConfigChanged={onSourceConfigChanged}
        onSaveAdvancedMode={onSaveModeAdvanced}
        nextFormStage={nextPage}
        onError={onError}
      />
    );
  }

  // Render SaveConfirmation modal alongside main content
  if (showSaveConfirmation) {
    return (
      <>
        {mainContent}
        <CustomUserConfirmation
          title="Save Changes to Active Source"
          message="You've made changes to an active source. Choose how you want to apply these changes:"
          okButtonLabel="Save for Next Run"
          cancelButtonLabel="Cancel"
          onConfirm={() => handleSaveConfirmation(true)}
          onClose={onCloseSaveConfirmation}
          yesWithChangesButtonLabel="Save & Run Now"
          onRunWithIngestion={() => handleSaveConfirmation(false)}
        />
      </>
    );
  }

  return mainContent;
};
